import type { AccessTokenPayload } from '../types/auth.types';

/**
 * Access token store — IN MEMORY ONLY.
 *
 * Security: the refresh token now lives in an httpOnly cookie the browser
 * sends automatically to /auth/refresh; JS never sees it. The short-lived
 * access token is held only in this module-level variable, so it's gone on a
 * full page reload (the app silently re-mints one via the refresh cookie) and
 * is never written to localStorage where an XSS could read it.
 */
let cachedAccessToken: string | null = null;

const LEGACY_ACCESS_KEY = 'access_token';
const LEGACY_REFRESH_KEY = 'refresh_token';
// Non-sensitive flag (just "1") so the SPA knows whether to attempt a silent
// refresh on load. Lets guests skip a doomed /auth/refresh round-trip. Safe to
// expose to JS — it carries no token.
const SESSION_HINT_KEY = 'session_hint';

export const tokenService = {
  getAccessToken: (): string | null => cachedAccessToken,

  setAccessToken: (token: string): void => {
    cachedAccessToken = token;
  },

  clearTokens: (): void => {
    cachedAccessToken = null;
    localStorage.removeItem(SESSION_HINT_KEY);
    // Also drop any tokens left in localStorage by the pre-cookie version.
    localStorage.removeItem(LEGACY_ACCESS_KEY);
    localStorage.removeItem(LEGACY_REFRESH_KEY);
  },

  hasAccessToken: (): boolean => !!cachedAccessToken,

  /** Mark that a session likely exists (called after a successful login). */
  setSessionHint: (): void => {
    localStorage.setItem(SESSION_HINT_KEY, '1');
  },

  /**
   * Whether to attempt a silent refresh on startup: true if we set the hint
   * on a prior login, or a legacy localStorage refresh token is still around
   * (pre-cookie build migrating over).
   */
  shouldAttemptRefresh: (): boolean => {
    return (
      localStorage.getItem(SESSION_HINT_KEY) === '1' ||
      !!localStorage.getItem(LEGACY_REFRESH_KEY)
    );
  },

  /**
   * One-time migration bridge: a refresh token left in localStorage by the
   * old build. Used once at startup to mint the httpOnly cookie, then cleared.
   */
  takeLegacyRefreshToken: (): string | null => {
    const token = localStorage.getItem(LEGACY_REFRESH_KEY);
    if (token) {
      localStorage.removeItem(LEGACY_REFRESH_KEY);
      localStorage.removeItem(LEGACY_ACCESS_KEY);
    }
    return token;
  },

  decodeToken: (token: string): AccessTokenPayload | null => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1]));
      return payload as AccessTokenPayload;
    } catch {
      return null;
    }
  },

  isTokenExpired: (token: string): boolean => {
    const payload = tokenService.decodeToken(token);
    if (!payload) return true;
    // Add 30 second buffer for network latency
    return payload.exp * 1000 < Date.now() + 30000;
  },

  isAccessTokenExpired: (): boolean => {
    if (!cachedAccessToken) return true;
    return tokenService.isTokenExpired(cachedAccessToken);
  },

  getTokenExpiry: (token: string): number | null => {
    const payload = tokenService.decodeToken(token);
    if (!payload) return null;
    return payload.exp * 1000;
  },

  shouldRefreshToken: (): boolean => {
    if (!cachedAccessToken) return false;
    const payload = tokenService.decodeToken(cachedAccessToken);
    if (!payload) return false;
    // Refresh if the token expires within 5 minutes
    const fiveMinutes = 5 * 60 * 1000;
    return payload.exp * 1000 < Date.now() + fiveMinutes;
  },
};
